/**
 * localStorage Vault Service
 * Manages the user's verified eclipse glasses collection on-device.
 *
 * Backwards-compatible: on first load, migrates entries from the legacy
 * `eclipse_verifications` key (used by the old scan page) into the new
 * `my_eclipse_wallet` format so users don't lose their collection.
 */

const VAULT_KEY = 'my_eclipse_wallet';
const LEGACY_KEY = 'eclipse_verifications';
const MIGRATED_FLAG = 'vault_migrated_v1';

/**
 * Neutral label shown when a code has no real product paired to it.
 * Codes are not yet all paired to specific products, so the UI must not claim
 * a specific model (this was previously hardcoded as "EclipsePro Paper Series
 * Glasses" in 8 places). Single source of truth for the display fallback.
 */
export const PRODUCT_FALLBACK = 'Verified Product';

export interface VaultEntry {
  code: string;
  serialNumber: number | null;
  firstSeenAt: string; // ISO date
  lastSeenAt: string;  // ISO date
  scanCount: number;
  batchName?: string;
  productName?: string;

  // Manufacturing / compliance (from verify API)
  productionDate?: string;
  useByDate?: string;
  manufacturingLot?: string;
  productionLine?: string;
  qualityCheck?: boolean;
  operatorName?: string;
  operatorDepartment?: string;
  batchCreatedAt?: string;

  // Ownership / client
  clientName?: string;
  assignmentType?: string;

  /** @deprecated use productName */
  edition?: string;
}

/** Legacy format from src/utils/verification.ts */
interface LegacyVerification {
  id: string;
  code?: string;
  status?: string;
  dateVerified: string;
  serialNumber?: string;
  scanCount?: number;
  verified: boolean;
}

/**
 * One-time migration: convert legacy eclipse_verifications → my_eclipse_wallet
 */
function migrateLegacyIfNeeded(): void {
  if (typeof window === 'undefined') return;
  if (localStorage.getItem(MIGRATED_FLAG)) return;

  try {
    const legacyRaw = localStorage.getItem(LEGACY_KEY);
    if (!legacyRaw) {
      localStorage.setItem(MIGRATED_FLAG, '1');
      return;
    }

    const legacy: LegacyVerification[] = JSON.parse(legacyRaw);
    const existingVault = getRawVault();
    const existingCodes = new Set(existingVault.map((e) => e.code));

    for (const item of legacy) {
      if (!item.code || !item.verified) continue;
      if (existingCodes.has(item.code)) continue;

      existingVault.push({
        code: item.code,
        serialNumber: item.serialNumber ? parseInt(item.serialNumber, 10) || null : null,
        firstSeenAt: item.dateVerified || new Date().toISOString(),
        lastSeenAt: item.dateVerified || new Date().toISOString(),
        scanCount: item.scanCount || 1,
        // No productName: legacy entries have no real paired product, so the UI
        // falls back to PRODUCT_FALLBACK rather than a fabricated model name.
      });
    }

    localStorage.setItem(VAULT_KEY, JSON.stringify(existingVault));
    localStorage.setItem(MIGRATED_FLAG, '1');
  } catch {
    // Migration failed — not critical, mark as done to avoid retrying
    localStorage.setItem(MIGRATED_FLAG, '1');
  }
}

/** Raw read without migration (used internally to avoid recursion) */
function getRawVault(): VaultEntry[] {
  try {
    const raw = localStorage.getItem(VAULT_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

export function getVault(): VaultEntry[] {
  if (typeof window === 'undefined') return [];
  migrateLegacyIfNeeded();
  return getRawVault();
}

export function isInVault(code: string): boolean {
  return getVault().some((e) => e.code === code);
}

export function getVaultEntry(code: string): VaultEntry | undefined {
  return getVault().find((e) => e.code === code);
}

export function addToVault(entry: VaultEntry): void {
  if (typeof window === 'undefined') return;
  const vault = getVault();
  const existing = vault.findIndex((e) => e.code === entry.code);
  if (existing >= 0) {
    vault[existing] = { ...vault[existing], ...entry, lastSeenAt: new Date().toISOString() };
  } else {
    vault.unshift(entry);
  }
  localStorage.setItem(VAULT_KEY, JSON.stringify(vault));
}

export function updateVaultEntry(code: string, updates: Partial<VaultEntry>): void {
  if (typeof window === 'undefined') return;
  const vault = getVault();
  const idx = vault.findIndex((e) => e.code === code);
  if (idx >= 0) {
    vault[idx] = { ...vault[idx], ...updates };
    localStorage.setItem(VAULT_KEY, JSON.stringify(vault));
  }
}

export function getVaultCount(): number {
  return getVault().length;
}
